import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Activity, BarChart3, FileSearch, AudioWaveform, Brain, TerminalSquare, Crown, LineChart, FlaskConical, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Iteration 84 — Hidden Developer Panel (role === "developer" only).
 *
 * For every other user these routes behave as if they DO NOT EXIST:
 * the guard silently redirects to "/" exactly like the app's catch-all
 * route. No locked states, no placeholders, no hints.
 *
 * ARCHITECTURE ONLY: the section pages are intentional stubs that future
 * iterations will populate (see /app/memory/PRD.md, Iter 84).
 */

export const DeveloperRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" />;
  // Behaves identically to the catch-all "*" route — non-developers cannot
  // distinguish /dev/* from any other non-existent URL.
  if (!user || user.role !== "developer") return <Navigate to="/" replace />;
  return children;
};

const SECTIONS = [
  { path: "/dev/analytics", label: "📊 Product Analytics", icon: LineChart, blurb: "Live product metrics, growth, engagement and business analytics." },
  { path: "/dev/testing", label: "🧪 Testing Controls", icon: FlaskConical, blurb: "Force relationship, mood, scenario and recall — instant Beta validation." },
  { path: "/dev/sessions", label: "Session Diagnostics", icon: Activity, blurb: "Per-session generation inspection — words, clusters, timings." },
  { path: "/dev/metrics", label: "Generation Metrics", icon: BarChart3, blurb: "Aggregate script-budget and pipeline metrics." },
  { path: "/dev/prompts", label: "Prompt Inspector", icon: FileSearch, blurb: "LLM prompt → raw response → sanitized → final timeline → TTS input." },
  { path: "/dev/audio", label: "Audio Pipeline", icon: AudioWaveform, blurb: "Preload status, cache hit rate, loaded assets, playback timing." },
  { path: "/dev/memory", label: "Memory Inspector", icon: Brain, blurb: "Companion memories and continuity state." },
  { path: "/dev/inner-life", label: "Inner Life", icon: Sparkles, blurb: "Pre-session inner state & independent life events per companion." },
  { path: "/dev/subscriptions", label: "Subscriptions", icon: Crown, blurb: "Grant or revoke Premium manually — no payments until after beta." },
];

const Shell = ({ title, backTo = "/dev", children }) => {
  const nav = useNavigate();
  return (
    <div className="relative z-10 min-h-screen px-6 pt-safe-top pb-safe-bottom max-w-3xl mx-auto text-violet-100/90">
      <div className="flex items-center gap-3 mb-8">
        {/* Iter 86 — back must always leave the current page: subpages return
            to the panel index, the index returns to Settings (previously the
            index arrow linked to itself, trapping the user). */}
        <button
          type="button"
          onClick={() => nav(backTo)}
          className="text-violet-300/60 hover:text-violet-200 transition-colors"
          data-testid="dev-back-link"
        >
          <ArrowLeft size={18} />
        </button>
        <TerminalSquare size={18} className="text-emerald-300/70" />
        <h1 className="text-lg font-medium tracking-wide" data-testid="dev-page-title">{title}</h1>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-300/50 border border-emerald-300/20 rounded-full px-2 py-0.5">developer</span>
      </div>
      {children}
    </div>
  );
};

export default function DeveloperPanel() {
  return (
    <Shell title="Developer Panel" backTo="/settings">
      <div className="grid gap-3" data-testid="dev-panel-sections">
        {SECTIONS.map(({ path, label, icon: Icon, blurb }) => (
          <Link
            key={path}
            to={path}
            data-testid={`dev-link-${path.split("/").pop()}`}
            className="group flex items-start gap-4 rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-4 hover:border-emerald-300/30 transition-colors"
          >
            <Icon size={18} className="mt-0.5 text-emerald-300/60 group-hover:text-emerald-300 transition-colors" />
            <div>
              <div className="text-sm font-medium">{label}</div>
              <div className="text-xs text-violet-300/50 mt-0.5">{blurb}</div>
            </div>
          </Link>
        ))}
      </div>
    </Shell>
  );
}

const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtNum = (v) => (v == null ? "—" : v);
const fmtMin = (v) => (v == null ? "—" : `${v} min`);
const fmtBool = (v) => (v == null ? "—" : v ? "yes" : "no");

const KV = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-violet-300/5 text-sm">
    <span className="text-violet-300/50">{label}</span>
    <span className="text-violet-100/90 text-right">{value}</span>
  </div>
);

export const DevSessionDiagnostics = () => {
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get("/dev/diagnostics/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
  }, []);
  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/dev/diagnostics/sessions/${selected}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [selected]);
  const m = detail?.metrics || {};
  return (
    <Shell title="Session Diagnostics">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-3 max-h-[70vh] overflow-y-auto" data-testid="dev-diag-session-list">
          {sessions == null && <p className="text-xs text-violet-300/40 p-2">Loading…</p>}
          {sessions?.length === 0 && <p className="text-xs text-violet-300/40 p-2">No sessions.</p>}
          {sessions?.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelected(s.id)}
              data-testid={`dev-diag-session-${s.id}`}
              className={`w-full text-left rounded-lg px-3 py-2 text-xs transition-colors ${selected === s.id ? "bg-emerald-300/10 text-emerald-200" : "hover:bg-violet-300/5 text-violet-200/70"}`}
            >
              <div className="font-mono">{s.label || `${s.id.slice(0, 8)} · ${s.character_id} · ${s.mode}`}</div>
              <div className="text-[10px] text-violet-300/40 mt-0.5">{s.created_at} · {s.status} · {s.duration_minutes ?? "?"} min</div>
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-4" data-testid="dev-diag-detail">
          {!selected && <p className="text-xs text-violet-300/40">Select a session.</p>}
          {selected && !detail && <p className="text-xs text-violet-300/40">Loading…</p>}
          {detail && (
            <>
              <p className="text-xs font-mono text-emerald-300/60 mb-3">{detail.label || detail.session_id}</p>
              <KV label="Atmosphere" value={detail.atmosphere_id || "—"} />
              <KV label="Companion" value={detail.character_id || "—"} />
              <KV label="Relationship level" value={detail.relationship_stage || "—"} />
              <KV label="Session duration" value={fmtMin(m.session_duration_min)} />
              <KV label="Dialogue target" value={fmtMin(detail.dialogue_duration_target_min)} />
              <KV label="Projected dialogue" value={fmtMin(detail.projected_dialogue_duration_min)} />
              <KV label="Words generated" value={fmtNum(m.words_generated)} />
              <KV label="Characters generated" value={fmtNum(m.characters_generated)} />
              <KV label="Dialogue clusters" value={fmtNum(m.dialogue_cluster_count)} />
              <KV label="Quiet beats" value={fmtNum(m.quiet_beat_count)} />
              <KV label="Presence events" value={fmtNum(m.presence_event_count)} />
              <KV label="Character events" value={fmtNum(m.character_event_count)} />
              <KV label="Extension pass" value={fmtBool(m.extension_pass_executed)} />
              <KV label="Clusters inserted" value={fmtNum(m.additional_clusters_inserted)} />
              <KV label="LLM generation" value={fmtMs(m.llm_generation_ms)} />
              <KV label="TTS generation" value={fmtMs(m.tts_generation_ms)} />
              <KV label="Credits consumed" value={fmtNum(m.total_credits_consumed)} />
            </>
          )}
        </div>
      </div>
    </Shell>
  );
};

export const DevGenerationMetrics = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/dev/metrics").then((r) => setRows(r.data.sessions || [])).catch(() => setRows([]));
  }, []);
  const cols = [
    ["Session", (r) => r.label || `${r.session_id.slice(0, 8)} · ${r.character_id || "?"}`],
    ["Mode", (r) => r.mode || "—"],
    ["Dur", (r) => fmtMin(r.session_duration_min)],
    ["Target", (r) => fmtMin(r.dialogue_duration_target_min)],
    ["Proj.", (r) => fmtMin(r.projected_dialogue_duration_min)],
    ["Words", (r) => fmtNum(r.words_generated)],
    ["Chars", (r) => fmtNum(r.characters_generated)],
    ["Clusters", (r) => fmtNum(r.dialogue_cluster_count)],
    ["Beats", (r) => fmtNum(r.quiet_beat_count)],
    ["Events", (r) => fmtNum(r.character_event_count)],
    ["Ext.", (r) => fmtBool(r.extension_pass_executed)],
    ["LLM", (r) => fmtMs(r.llm_generation_ms)],
    ["TTS", (r) => fmtMs(r.tts_generation_ms)],
    ["Credits", (r) => fmtNum(r.total_credits_consumed)],
  ];
  return (
    <Shell title="Generation Metrics">
      <div className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-4 overflow-x-auto" data-testid="dev-metrics-table">
        {rows == null && <p className="text-xs text-violet-300/40">Loading…</p>}
        {rows?.length === 0 && <p className="text-xs text-violet-300/40">No sessions.</p>}
        {rows?.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-violet-300/40">
                {cols.map(([h]) => <th key={h} className="pb-2 pr-3 font-normal whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.session_id} className="border-t border-violet-300/5 text-violet-100/80" data-testid={`dev-metrics-row-${r.session_id}`}>
                  {cols.map(([h, fn]) => <td key={h} className="py-1.5 pr-3 whitespace-nowrap font-mono">{fn(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
};
const Pre = ({ text, testid }) => (
  <pre data-testid={testid} className="text-[11px] leading-relaxed text-violet-100/80 font-mono whitespace-pre-wrap break-words max-h-80 overflow-y-auto bg-black/20 rounded-lg p-3">
    {text || "—"}
  </pre>
);

const Section = ({ title, children, tone = "default" }) => (
  <div className={`rounded-xl border p-4 mb-3 backdrop-blur-md ${tone === "error" ? "border-red-400/30 bg-red-950/20" : "border-violet-300/10 bg-violet-950/20"}`}>
    <div className={`text-[10px] uppercase tracking-widest mb-2 ${tone === "error" ? "text-red-300/70" : "text-emerald-300/50"}`}>{title}</div>
    {children}
  </div>
);

export const DevPromptInspector = () => {
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [trace, setTrace] = useState(null);
  useEffect(() => {
    api.get("/dev/diagnostics/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
  }, []);
  useEffect(() => {
    if (!selected) return;
    setTrace(null);
    api.get(`/dev/prompt-inspector/${selected}`).then((r) => setTrace(r.data)).catch(() => setTrace(null));
  }, [selected]);
  const tlText = (tl) => (tl || []).map((s) => `[${s.at_sec ?? "?"}s · ${s.kind || "?"}${s.silence_kind ? ` · silence:${s.silence_kind}` : ""}]\n${s.text || ""}`).join("\n\n");
  return (
    <Shell title="Prompt Inspector">
      <div className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-3 mb-4 max-h-48 overflow-y-auto" data-testid="dev-prompt-session-list">
        {sessions == null && <p className="text-xs text-violet-300/40 p-2">Loading…</p>}
        {sessions?.length === 0 && <p className="text-xs text-violet-300/40 p-2">No sessions.</p>}
        {sessions?.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelected(s.id)}
            data-testid={`dev-prompt-session-${s.id}`}
            className={`w-full text-left rounded-lg px-3 py-1.5 text-xs transition-colors ${selected === s.id ? "bg-emerald-300/10 text-emerald-200" : "hover:bg-violet-300/5 text-violet-200/70"}`}
          >
            <span className="font-mono">{s.label || `${s.id.slice(0, 8)} · ${s.character_id} · ${s.created_at}`}</span>
          </button>
        ))}
      </div>
      {!selected && <p className="text-xs text-violet-300/40">Select a session to inspect its generation pipeline.</p>}
      {selected && !trace && <p className="text-xs text-violet-300/40">Loading trace…</p>}
      {trace && (
        <div data-testid="dev-prompt-trace">
          {trace.label && (
            <p className="text-xs font-mono text-emerald-300/60 mb-3" data-testid="dev-prompt-trace-label">{trace.label}</p>
          )}
          {(trace.sanitizer_errors || []).length > 0 && (
            <Section title={`Sanitizer errors (${trace.sanitizer_errors.length})`} tone="error">
              <Pre testid="dev-trace-sanitizer-errors" text={JSON.stringify(trace.sanitizer_errors, null, 2)} />
            </Section>
          )}
          <Section title="0 · System Prompt"><Pre testid="dev-trace-system-prompt" text={trace.llm_system_prompt || "Not recorded (pre-update session)."} /></Section>
          <Section title="1 · LLM Prompt"><Pre testid="dev-trace-prompt" text={trace.llm_prompt} /></Section>
          <Section title="1b · Presence Lines (2nd pass — idle/world murmurs spoken during silences)">
            <Pre testid="dev-trace-presence-lines" text={trace.presence_lines ? JSON.stringify(trace.presence_lines, null, 2) : "None recorded for this session."} />
          </Section>
          <Section title="1c · Live Reactive Lines (mid-session LLM lines)">
            <Pre testid="dev-trace-reactive-lines" text={trace.reactive_lines?.length ? JSON.stringify(trace.reactive_lines, null, 2) : "None spoken in this session."} />
          </Section>
          <Section title="2 · Raw LLM Output"><Pre testid="dev-trace-raw" text={trace.llm_raw_response} /></Section>
          <Section title="3 · Sanitized Output"><Pre testid="dev-trace-sanitized" text={tlText(trace.sanitized_timeline)} /></Section>
          <Section title="4 · Final Dialogue Timeline"><Pre testid="dev-trace-final" text={tlText(trace.final_timeline)} /></Section>
          <Section title="5 · TTS Input">
            <Pre testid="dev-trace-tts" text={(trace.tts_inputs || []).map((t) => `[#${t.segment_index} · ${t.kind || "?"}${t.rejected ? " · REJECTED " + (t.sanitizer_reasons || []).join(",") : ""}]\n${t.text || ""}`).join("\n\n") || (trace.tts_inputs === null ? "TTS pre-render still in flight…" : "—")} />
          </Section>
          <Section title="Memories injected"><Pre testid="dev-trace-memories" text={JSON.stringify(trace.memories_injected || [], null, 2)} /></Section>
        </div>
      )}
    </Shell>
  );
};

export const DevAudioPipeline = () => {
  const ATMOS = ["cozy_cabin", "forest_campfire", "rainy_cabin", "seaside_evening"];
  const [atmo, setAtmo] = useState("cozy_cabin");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/dev/audio-pipeline", { params: { atmosphere_id: atmo } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [atmo]);
  return (
    <Shell title="Audio Pipeline">
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="dev-audio-atmo-picker">
        {ATMOS.map((a) => (
          <button key={a} onClick={() => setAtmo(a)} data-testid={`dev-audio-atmo-${a}`}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${atmo === a ? "border-emerald-300/40 text-emerald-200 bg-emerald-300/10" : "border-violet-300/15 text-violet-200/60 hover:border-violet-300/30"}`}>
            {a}
          </button>
        ))}
      </div>
      {!data && <p className="text-xs text-violet-300/40">Loading…</p>}
      {data && (
        <div data-testid="dev-audio-pipeline-data">
          <Section title="Selected atmosphere">
            <KV label="Atmosphere" value={data.atmosphere?.id} />
            <KV label="Quality tier" value={data.atmosphere?.quality_tier} />
            <KV label="Loop categories" value={(data.atmosphere?.loops || []).join(", ") || "—"} />
            <KV label="Event categories" value={(data.atmosphere?.events || []).join(", ") || "—"} />
          </Section>
          <Section title={`Ambience loops (${(data.ambience_loops || []).length})`}>
            <Pre testid="dev-audio-loops" text={JSON.stringify(data.ambience_loops, null, 2)} />
          </Section>
          <Section title={`Ambience events (${(data.ambience_events || []).length})`}>
            <Pre testid="dev-audio-events" text={JSON.stringify(data.ambience_events, null, 2)} />
          </Section>
          <Section title="Presence events (shared)">
            <Pre testid="dev-audio-presence" text={JSON.stringify({ assets: data.presence_events, policy: data.presence_event_policy }, null, 2)} />
          </Section>
          <Section title="Character events (Serine)">
            <Pre testid="dev-audio-character" text={JSON.stringify(data.character_events, null, 2)} />
          </Section>
          <Section title="Playback schedule">
            <Pre testid="dev-audio-schedule" text={JSON.stringify(data.playback_schedule, null, 2)} />
          </Section>
          <Section title="Gains & fades">
            {Object.entries(data.gains_and_fades || {}).map(([k, v]) => <KV key={k} label={k} value={String(v)} />)}
          </Section>
          <Section title="TTS cache">
            <KV label="Cached files" value={data.tts_cache?.files} />
            <KV label="Total size" value={`${data.tts_cache?.total_mb} MB`} />
          </Section>
        </div>
      )}
    </Shell>
  );
};

export const DevMemoryInspector = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dev/memory-inspector").then((r) => setData(r.data.users || [])).catch(() => setData([]));
  }, []);
  return (
    <Shell title="Memory Inspector">
      {data == null && <p className="text-xs text-violet-300/40">Loading…</p>}
      {data?.length === 0 && <p className="text-xs text-violet-300/40">No users.</p>}
      {data?.map((u) => (
        <div key={u.user.id} className="mb-6" data-testid={`dev-memory-user-${u.user.id}`}>
          <div className="text-sm font-medium mb-2 text-emerald-200/80">{u.user.email} <span className="text-violet-300/40">· {u.user.user_name || "—"} · {u.user.active_character || "no companion"}</span></div>
          <Section title="Relationship level">
            <KV label="Stage (active)" value={u.relationship?.stage || "—"} />
            <KV label="Sessions (active)" value={u.relationship?.session_count ?? "—"} />
            {Object.entries(u.relationship?.per_companion || {}).map(([cid, t]) => (
              <KV key={cid} label={`↳ ${cid}`} value={`${t.stage || "?"} · ${t.sessions ?? "?"} sessions · since ${t.first_session_at ? t.first_session_at.slice(0, 10) : "?"}`} />
            ))}
          </Section>
          <Section title={`Stored memories (${(u.stored_memories || []).length} rolling · ${(u.pinned_memories || []).length} pinned)`}>
            <Pre testid={`dev-memory-stored-${u.user.id}`} text={JSON.stringify({ rolling: u.stored_memories, pinned: u.pinned_memories }, null, 2)} />
          </Section>
          <Section title="Last generation — injected memories">
            <Pre testid={`dev-memory-injected-${u.user.id}`} text={u.last_generation?.injected_memories ? JSON.stringify(u.last_generation.injected_memories, null, 2) : "No trace recorded yet (pre-Iter-87 session or no sessions)."} />
          </Section>
          <Section title="Continuity state">
            <KV label="Last session" value={u.continuity?.last_session_at || "—"} />
            <KV label="Last summary" value={(u.continuity?.last_summary || "—").slice(0, 120)} />
            <KV label="Preferences" value={Object.keys(u.preferences || {}).length ? JSON.stringify(u.preferences).slice(0, 120) : "—"} />
          </Section>
        </div>
      ))}
    </Shell>
  );
};

// Natural Presence — inner state & independent life events inspector.
export const DevInnerLife = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dev/inner-life").then((r) => setData(r.data.users || [])).catch(() => setData([]));
  }, []);
  return (
    <Shell title="Inner Life">
      <p className="text-xs text-violet-300/50 mb-4">
        Pre-session inner state (premise) of the latest evening + each companion's independent life events.
        Most sessions should have NO event — that is by design (~30% roll, ~50% of those mentionable).
      </p>
      {data == null && <p className="text-xs text-violet-300/40">Loading…</p>}
      {data?.length === 0 && <p className="text-xs text-violet-300/40">No users.</p>}
      {data?.map((u) => (
        <div key={u.user.id} className="mb-6" data-testid={`dev-innerlife-user-${u.user.id}`}>
          <div className="text-sm font-medium mb-2 text-emerald-200/80">
            {u.user.email} <span className="text-violet-300/40">· {u.user.user_name || "—"}</span>
          </div>
          <Section title={`Last evening inner state (${u.last_session_inner_state?.character_id || "—"} · ${u.last_session_inner_state?.created_at ? u.last_session_inner_state.created_at.slice(0, 16) : "no sessions"})`}>
            <Pre testid={`dev-innerlife-premise-${u.user.id}`} text={u.last_session_inner_state?.premise ? JSON.stringify(u.last_session_inner_state.premise, null, 2) : "No premise recorded yet."} />
          </Section>
          {Object.entries(u.per_companion || {}).map(([cid, t]) => (
            <Section key={cid} title={`${cid} — life events (${(t.life_events || []).length}) · ${t.session_count ?? 0} sessions`}>
              {(t.life_events || []).length === 0 && (
                <p className="text-xs text-violet-300/40">No open life events — ordinary life.</p>
              )}
              {(t.life_events || []).map((ev, i) => (
                <div key={i} className="py-1.5 border-b border-violet-300/5 text-xs">
                  <span className={ev.mentionable ? "text-emerald-300/70" : "text-violet-300/50"}>
                    [{ev.mentionable ? "mentionable" : "private"}]
                  </span>{" "}
                  <span className="text-violet-100/80">{ev.text}</span>
                  <span className="text-violet-300/40"> · {ev.sessions_old ?? 0} sessions old · offered {ev.times_offered ?? 0}×</span>
                </div>
              ))}
              {t.unfinished_thread?.text && (
                <KV label="Unfinished thread" value={t.unfinished_thread.text.slice(0, 100)} />
              )}
            </Section>
          ))}
        </div>
      ))}
    </Shell>
  );
};

// Iter 88 — manual Premium grant/revoke until payments ship after beta.
export const DevSubscriptions = () => {
  const [users, setUsers] = useState(null);
  const [busyEmail, setBusyEmail] = useState(null);

  const load = () => {
    api.get("/dev/subscriptions").then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
  };
  useEffect(load, []);

  const setTier = async (email, tier) => {
    if (busyEmail) return;
    setBusyEmail(email);
    try {
      await api.post("/dev/subscriptions/set", { email, tier });
      toast.success(`${email} → ${tier}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not update subscription.");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <Shell title="Subscriptions">
      <p className="text-xs text-violet-300/50 mb-4">
        Two tiers only: Free and Premium. Grants are manual during beta. The developer account has permanent lifetime Premium.
      </p>
      {users == null && <p className="text-xs text-violet-300/40">Loading…</p>}
      {users?.length === 0 && <p className="text-xs text-violet-300/40">No users.</p>}
      <div className="space-y-2" data-testid="dev-subscriptions-list">
        {users?.map((u) => (
          <div
            key={u.id}
            className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md px-4 py-3 flex items-center justify-between gap-3"
            data-testid={`dev-sub-row-${u.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm text-violet-100/90 truncate">
                {u.email}
                {u.role === "developer" && (
                  <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-300/70 border border-emerald-300/25 rounded-full px-1.5 py-0.5">dev</span>
                )}
              </p>
              <p className="text-[11px] text-violet-300/45">
                {u.user_name || "—"} · {u.session_count || 0} sessions · joined {(u.created_at || "").slice(0, 10)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className={`text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 border ${
                  u.tier === "premium"
                    ? "text-amber-200/90 border-amber-300/30 bg-amber-300/5"
                    : "text-violet-300/60 border-violet-300/20"
                }`}
                data-testid={`dev-sub-tier-${u.id}`}
              >
                {u.tier}{u.lifetime ? " · lifetime" : ""}
              </span>
              {u.source !== "developer" && (
                u.tier !== "free" ? (
                  <button
                    onClick={() => setTier(u.email, "free")}
                    disabled={busyEmail === u.email}
                    data-testid={`dev-sub-revoke-${u.id}`}
                    className="text-[10px] uppercase tracking-widest rounded-full px-3 py-1.5 border border-rose-300/30 text-rose-200/80 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
                  >
                    Revoke
                  </button>
                ) : (
                  <>
                  <button
                    onClick={() => setTier(u.email, "light")}
                    disabled={busyEmail === u.email}
                    data-testid={`dev-sub-grant-light-${u.id}`}
                    className="text-[10px] uppercase tracking-widest rounded-full px-3 py-1.5 border border-violet-300/30 text-violet-200/80 hover:bg-violet-300/10 transition-colors disabled:opacity-50"
                  >
                    Light
                  </button>
                  <button
                    onClick={() => setTier(u.email, "premium")}
                    disabled={busyEmail === u.email}
                    data-testid={`dev-sub-grant-${u.id}`}
                    className="text-[10px] uppercase tracking-widest rounded-full px-3 py-1.5 border border-amber-300/30 text-amber-200/80 hover:bg-amber-300/10 transition-colors disabled:opacity-50"
                  >
                    Premium
                  </button>
                  </>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
};

// Iter 99 — Product Analytics console. Null values render as a "No data yet"
// pill so backend instrumentation can land later without touching this page.
const Soon = () => (
  <span className="text-[10px] uppercase tracking-widest text-violet-300/40 border border-violet-300/15 rounded-full px-2 py-0.5">
    No data yet
  </span>
);

const Kpi = ({ label, value, testid }) => (
  <div
    className="rounded-2xl border border-violet-300/10 bg-violet-950/25 backdrop-blur-md p-5 flex flex-col gap-2"
    data-testid={testid}
  >
    <span className="text-[10px] uppercase tracking-[0.22em] text-violet-300/50">{label}</span>
    {value == null ? (
      <span className="mt-1"><Soon /></span>
    ) : (
      <span className="font-serif-display text-4xl sm:text-5xl leading-none text-violet-50">{value}</span>
    )}
  </div>
);

const Metric = ({ label, value, testid }) => (
  <div
    className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md px-4 py-3.5 flex items-center justify-between gap-3"
    data-testid={testid}
  >
    <span className="text-xs text-violet-300/55">{label}</span>
    {value == null ? <Soon /> : <span className="text-lg font-medium text-violet-50 whitespace-nowrap">{value}</span>}
  </div>
);

const AnalyticsSection = ({ title, children }) => (
  <div className="mb-8">
    <div className="text-[10px] uppercase tracking-widest text-emerald-300/50 mb-3">{title}</div>
    <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </div>
);

const fmtPct = (v) => (v == null ? null : `${v}%`);
const fmtCount = (v) => (v == null ? null : v.toLocaleString());

export const DevProductAnalytics = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get("/dev/analytics").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <Shell title="Product Analytics">
        <p className="text-xs text-red-300/70">Could not load analytics.</p>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell title="Product Analytics">
        <p className="text-xs text-violet-300/40">Loading metrics…</p>
      </Shell>
    );
  }

  const { overview, growth, sessions, relationships, premium, ai, audio, health, platforms } = data;
  return (
    <Shell title="Product Analytics">
      <div data-testid="dev-analytics-page">
        {/* Overview — primary KPIs */}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-10" data-testid="analytics-overview">
          <Kpi label="Total Accounts" value={fmtCount(overview.total_accounts)} testid="kpi-total-accounts" />
          <Kpi label="Premium Accounts" value={fmtCount(overview.premium_accounts)} testid="kpi-premium-accounts" />
          <Kpi label="Free Accounts" value={fmtCount(overview.free_accounts)} testid="kpi-free-accounts" />
          <Kpi label="Online Now" value={fmtCount(overview.online_now)} testid="kpi-online-now" />
        </div>

        <AnalyticsSection title="Growth">
          <Metric label="New Accounts Today" value={fmtCount(growth.new_today)} testid="metric-new-today" />
          <Metric label="New Accounts This Week" value={fmtCount(growth.new_week)} testid="metric-new-week" />
          <Metric label="New Accounts This Month" value={fmtCount(growth.new_month)} testid="metric-new-month" />
          <Metric label="Daily Active Users (DAU)" value={fmtCount(growth.dau)} testid="metric-dau" />
          <Metric label="Weekly Active Users (WAU)" value={fmtCount(growth.wau)} testid="metric-wau" />
          <Metric label="Monthly Active Users (MAU)" value={fmtCount(growth.mau)} testid="metric-mau" />
          <Metric label="Returning Users" value={fmtCount(growth.returning_users)} testid="metric-returning" />
        </AnalyticsSection>

        <AnalyticsSection title="Session Analytics">
          <Metric label="Sessions Started" value={fmtCount(sessions.started)} testid="metric-sessions-started" />
          <Metric label="Sessions Completed" value={fmtCount(sessions.completed)} testid="metric-sessions-completed" />
          <Metric label="Session Completion Rate" value={fmtPct(sessions.completion_rate_pct)} testid="metric-completion-rate" />
          <Metric label="Average Session Duration" value={sessions.avg_duration_min == null ? null : `${sessions.avg_duration_min} min`} testid="metric-avg-duration" />
          <Metric label="Average Words Generated" value={fmtCount(sessions.avg_words_generated)} testid="metric-avg-words" />
          <Metric label="Average Audio Duration" value={sessions.avg_audio_duration_min == null ? null : `${sessions.avg_audio_duration_min} min`} testid="metric-avg-audio" />
        </AnalyticsSection>

        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/50 mb-3">Relationship Analytics</div>
          <div className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-4" data-testid="analytics-relationships">
            {(relationships || []).length === 0 && <p className="text-xs text-violet-300/40">No relationship data yet.</p>}
            {(relationships || []).map((r) => (
              <div key={r.stage} className="py-2 border-b border-violet-300/5 last:border-b-0" data-testid={`relationship-row-${r.stage.replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-violet-200/80 capitalize">{r.stage}</span>
                  <span className="text-violet-300/55 font-mono">{r.count.toLocaleString()} · {r.pct}%</span>
                </div>
                <div className="h-1 rounded-full bg-violet-300/10 overflow-hidden">
                  <div className="h-full rounded-full bg-violet-300/50" style={{ width: `${r.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <AnalyticsSection title="Premium Analytics">
          <Metric label="Premium Accounts" value={fmtCount(premium.premium_accounts)} testid="metric-premium-count" />
          <Metric label="Free Accounts" value={fmtCount(premium.free_accounts)} testid="metric-free-count" />
          <Metric label="Premium Conversion" value={fmtPct(premium.conversion_pct)} testid="metric-conversion" />
          <Metric label="New Premium Today" value={fmtCount(premium.new_premium_today)} testid="metric-new-premium-today" />
          <Metric label="Cancelled Subscriptions" value={fmtCount(premium.cancelled_subscriptions)} testid="metric-cancelled-subs" />
          <Metric label="Monthly Recurring Revenue (MRR)" value={premium.mrr == null ? null : `$${premium.mrr}`} testid="metric-mrr" />
        </AnalyticsSection>

        <AnalyticsSection title="AI Analytics">
          <Metric label="Average Generation Time" value={ai.avg_generation_ms == null ? null : `${(ai.avg_generation_ms / 1000).toFixed(1)}s`} testid="metric-avg-generation" />
          <Metric label="Average Prompt Tokens" value={fmtCount(ai.avg_prompt_tokens)} testid="metric-prompt-tokens" />
          <Metric label="Average Response Tokens" value={fmtCount(ai.avg_response_tokens)} testid="metric-response-tokens" />
          <Metric label="Failed Generations" value={fmtCount(ai.failed_generations)} testid="metric-failed-generations" />
          <Metric label="Memory Retrieval Success" value={fmtPct(ai.memory_retrieval_success_pct)} testid="metric-memory-retrieval" />
          <Metric label="Memory Save Success" value={fmtPct(ai.memory_save_success_pct)} testid="metric-memory-save" />
        </AnalyticsSection>

        <AnalyticsSection title="Audio Analytics">
          <Metric label="Average Session Load Time" value={audio.avg_session_load_ms == null ? null : `${audio.avg_session_load_ms}ms`} testid="metric-load-time" />
          <Metric label="Cache Hit Rate" value={fmtPct(audio.cache_hit_rate_pct)} testid="metric-cache-hit" />
          <Metric label="Preload Success" value={fmtPct(audio.preload_success_pct)} testid="metric-preload" />
          <Metric label="Missing Assets" value={fmtCount(audio.missing_assets)} testid="metric-missing-assets" />
          <Metric label="Playback Errors" value={fmtCount(audio.playback_errors)} testid="metric-playback-errors" />
        </AnalyticsSection>

        <AnalyticsSection title="Application Health">
          <Metric label="Backend Errors" value={fmtCount(health.backend_errors)} testid="metric-backend-errors" />
          <Metric label="Network Errors" value={fmtCount(health.network_errors)} testid="metric-network-errors" />
          <Metric label="AI Errors" value={fmtCount(health.ai_errors)} testid="metric-ai-errors" />
          <Metric label="Audio Errors" value={fmtCount(health.audio_errors)} testid="metric-audio-errors" />
          <Metric label="Crash Rate" value={fmtPct(health.crash_rate_pct)} testid="metric-crash-rate" />
        </AnalyticsSection>

        <AnalyticsSection title="Platforms">
          <Metric label="iOS" value={fmtCount(platforms.ios)} testid="metric-platform-ios" />
          <Metric label="Android" value={fmtCount(platforms.android)} testid="metric-platform-android" />
          <Metric label="Web" value={fmtCount(platforms.web)} testid="metric-platform-web" />
        </AnalyticsSection>

        <p className="text-[10px] text-violet-300/30 text-center pb-4">
          Snapshot generated {data.generated_at?.slice(0, 19).replace("T", " ")} UTC
        </p>
      </div>
    </Shell>
  );
};

// Iter 102 — Developer Testing Controls. Generation-layer overrides applied
// server-side ONLY for role=developer accounts. Overrides persist until
// cleared here; production users are never affected.
const OVERRIDE_FIELDS = [
  {
    key: "relationship_stage",
    label: "Relationship Layer",
    hint: "Test warmth, intimacy and closeness without weeks of progression.",
    labels: { stranger: "Stranger", familiar_visitor: "Familiar Visitor", trusted_companion: "Trusted Companion", deep_attachment: "Deep Attachment" },
  },
  {
    key: "character_state",
    label: "Mood Layer",
    hint: "Force the companion's emotional state for this session.",
    labels: { neutral: "Neutral", vulnerable: "Vulnerable", sleepy: "Sleepy", worried: "Worried (Elizabeth)", caring: "Caring (Elizabeth)" },
  },
  {
    key: "night_scenario",
    label: "Night Scenario / Arrival",
    hint: "Force the arrival + whole-session directive (sleep and morning).",
    labels: {
      peaceful: "Peaceful", restless: "Restless", half_asleep: "Half Asleep", already_asleep: "Already Asleep",
      gentle_waking: "Gentle Waking (morning)", still_dreaming: "Still Dreaming (morning)", quiet_sunlight: "Quiet Sunlight (morning)",
    },
  },
  {
    key: "recall",
    label: "Recall Override",
    hint: "Shared Journey recall: force it, disable it, or leave to probability + cooldown.",
    labels: { auto: "Auto (normal behavior)", force: "Force Recall", disable: "Disable Recall" },
  },
];

export const DevTestingControls = () => {
  const [options, setOptions] = useState(null);
  const [values, setValues] = useState({});
  const [active, setActive] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  const load = () => api.get("/dev/overrides").then((r) => {
    setOptions(r.data.options);
    setActive(r.data.overrides || {});
    setValues({ ...r.data.overrides });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/dev/overrides", payload);
      setActive(data.overrides || {});
      setValues({ ...data.overrides });
      toast.success(Object.keys(data.overrides || {}).length ? "Overrides active." : "All overrides cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save overrides.");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = Object.keys(active).length;
  return (
    <Shell title="Testing Controls">
      <div data-testid="dev-testing-page">
        <div className="rounded-xl border border-amber-300/20 bg-amber-500/5 px-4 py-3 mb-6 text-xs text-amber-200/80" data-testid="testing-overrides-status">
          {activeCount
            ? `⚠ ${activeCount} override${activeCount > 1 ? "s" : ""} ACTIVE — every session you generate uses them until cleared.`
            : "No overrides active — sessions use normal production behavior."}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="testing-panel-toggle"
          className="text-[10px] uppercase tracking-widest text-emerald-300/50 mb-3 hover:text-emerald-200/70"
        >
          {open ? "▾ Generation Overrides" : "▸ Generation Overrides"}
        </button>

        {open && options && (
          <div className="space-y-5">
            {OVERRIDE_FIELDS.map((f) => (
              <div key={f.key} className="rounded-xl border border-violet-300/10 bg-violet-950/20 backdrop-blur-md p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm text-violet-100">{f.label}</p>
                    <p className="text-[11px] text-violet-300/45 mt-0.5">{f.hint}</p>
                  </div>
                  <select
                    value={values[f.key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    data-testid={`override-select-${f.key}`}
                    className="bg-indigo-950/60 border border-violet-300/20 rounded-lg px-3 py-2 text-sm text-violet-50 focus:outline-none focus:border-violet-300/50"
                  >
                    <option value="">{f.key === "recall" ? "Auto (normal behavior)" : "Auto (no override)"}</option>
                    {(options[f.key] || []).filter((o) => o !== "auto").map((o) => (
                      <option key={o} value={o}>{f.labels[o] || o}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              <button
                type="button" disabled={saving}
                onClick={() => save(values)}
                data-testid="overrides-save-button"
                className="rounded-full px-6 py-2.5 bg-violet-200 text-indigo-950 text-sm font-medium hover:bg-violet-100 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Apply overrides"}
              </button>
              <button
                type="button" disabled={saving}
                onClick={() => save({})}
                data-testid="overrides-clear-button"
                className="rounded-full px-6 py-2.5 border border-violet-300/30 text-violet-200 text-sm hover:border-violet-200/60 transition-colors disabled:opacity-50"
              >
                Clear all
              </button>
            </div>

            <p className="text-[11px] text-violet-300/40 leading-relaxed">
              Applied at generation time, developer accounts only. The session doc records
              <span className="font-mono"> dev_overrides_applied</span> so the Prompt Inspector shows exactly what was forced.
              Force Recall skips the cooldown write — it never disturbs the production recall rhythm.
            </p>
          </div>
        )}
      </div>
    </Shell>
  );
};
